import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArtEngine } from './engine';
import { StudioCanvas, useReducedMotion, type Tool } from './StudioCanvas';
import { BRUSH_SIZES, EXTRA_PALETTE, PRIMARY_PALETTE, swatchName } from './palette';
import {
  FINISHES,
  MEDIA,
  fillStyleFor,
  medium,
  paintSpec,
  parsePaint,
  type BrushKind,
  type Finish,
} from './media';
import { EraserObject, FillObject, Implement } from './Implements';
import { IconBack, IconCheck, IconRedo, IconUndo } from './Icons';
import { Button } from '@/design-system/Button';
import { cn } from '@/design-system/cn';
import { Feedback } from '@/services/feedback';
import type { ColoringTemplate } from '@/contracts/content';
import type { SaveState } from '@/services/artwork';

interface Props {
  template: ColoringTemplate;
  engine: ArtEngine;
  saveState: SaveState;
  onRequestSave: () => void;
  onDone: () => void;
  onBack: () => void;
  busy?: boolean;
  /** True on tablets/desktop — tools move to a side rail. */
  wide: boolean;
  handedness: 'right' | 'left';
  /** Bedtime: quieter palette, effects and sound off. */
  calm?: boolean;
}

/**
 * The creative studio (spec §7 C08).
 *
 * Two things make this feel like a real art table rather than a form:
 *
 *  - The tools are OBJECTS. A marker, a pencil, a brush, a crayon — each drawn
 *    as itself, each carrying the child's current colour in its barrel, and the
 *    chosen one slides out of the tray the way you pull a pen from a pot. A
 *    child who cannot read still knows which one is the glittery one.
 *
 *  - Every mark stays where it was started. Colouring on paper punishes a hand
 *    that cannot hold a line; this does not. It is on by default and can be
 *    switched off by anyone who would rather draw freely.
 *
 * Layout follows the device: a bottom tool table on a phone, a side rail with a
 * handedness preference on iPad and desktop.
 */
export function ColoringStudio({
  template,
  engine,
  saveState,
  onRequestSave,
  onDone,
  onBack,
  busy,
  wide,
  handedness,
  calm,
}: Props) {
  /**
   * A blank page has no authored regions, so filling a shape and staying inside
   * one are not just unhelpful there — they are meaningless. Rather than show
   * controls that would do nothing (spec §7 C06-C07), the studio drops them and
   * hands the child a pen.
   */
  const blankPage = template.regions.length === 0;

  // Fill is the first tool on a colouring page: one tap on a shape is the whole
  // idea of a colouring book, and it succeeds before a child has learned
  // anything. On a blank page there is nothing to tap, so a marker is.
  const [tool, setTool] = useState<Tool>(blankPage ? 'brush' : 'fill');
  const [brush, setBrush] = useState<BrushKind>('marker');
  const [hex, setHex] = useState(PRIMARY_PALETTE[4].hex);
  const [finish, setFinish] = useState<Finish>('solid');
  const [brushSize, setBrushSize] = useState(BRUSH_SIZES[1].value);
  const [zoom, setZoom] = useState(1);
  const [showMore, setShowMore] = useState(false);
  const [stayInside, setStayInside] = useState(true);
  const [, forceRender] = useState(0);
  const announce = useRef<HTMLParagraphElement>(null);

  const reduced = useReducedMotion();
  const quiet = calm || reduced;
  const paint = tool === 'fill' ? paintSpec(hex, finish) : hex;

  const bump = useCallback(() => forceRender((n) => n + 1), []);

  const handleOperation = useCallback(() => {
    bump();
    onRequestSave();
  }, [bump, onRequestSave]);

  // Sound and haptics are off entirely at bedtime — the point of bedtime is
  // that the app gets quieter, not that it swaps one kind of stimulation for
  // another (spec §8.4).
  useEffect(() => {
    Feedback.setEnabled(!calm);
  }, [calm]);

  // Keyboard support on web (spec §9.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) engine.redo();
        else engine.undo();
        bump();
        onRequestSave();
        return;
      }
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'f' && !blankPage) setTool('fill');
      if (e.key === 'b') setTool('brush');
      if (e.key === 'e') setTool('erase');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [engine, bump, onRequestSave, blankPage]);

  const swatches = useMemo(
    () => (showMore ? [...PRIMARY_PALETTE, ...EXTRA_PALETTE] : PRIMARY_PALETTE),
    [showMore],
  );

  const say = (text: string) => {
    if (announce.current) announce.current.textContent = text;
  };

  const pickMedium = (kind: BrushKind) => {
    setBrush(kind);
    setTool('brush');
    Feedback.pick();
    say(`${medium(kind).name} — ${medium(kind).hint}`);
  };

  /* ---------------- The tool table ---------------- */

  const MediaTray = (
    <div
      role="group"
      aria-label="Drawing tools"
      className={cn(
        'gap-xs',
        // Two columns in the rail: eight implements in a single column push
        // the colours off the bottom of an iPad, and the colours are used far
        // more often than the eighth pen.
        wide
          ? 'grid grid-cols-2 rounded-blob border-2 border-line bg-paper-deep p-s'
          : 'flex overflow-x-auto rounded-blob border-2 border-line bg-paper-deep p-s',
      )}
    >
      {MEDIA.map((m) => {
        const active = tool === 'brush' && brush === m.kind;
        return (
          <button
            key={m.kind}
            onClick={() => pickMedium(m.kind)}
            aria-pressed={active}
            aria-label={m.name}
            title={m.hint}
            className={cn(
              'group relative shrink-0 rounded-pebble transition-transform duration-200 ease-out',
              wide ? 'h-[40px] w-full' : 'h-[44px] w-[100px]',
              // Pulled from the pot. On a phone the tray is horizontal, so it
              // lifts instead of sliding sideways.
              active
                ? wide
                  ? 'scale-[1.12] drop-shadow-[0_3px_0_rgba(44,28,56,0.25)]'
                  : '-translate-y-[6px] scale-[1.06]'
                : 'opacity-80 hover:opacity-100',
            )}
          >
            <Implement kind={m.kind} ink={m.ownColor ? m.barrel : hex} />
            <span className="u-sr">{m.name}</span>
          </button>
        );
      })}
    </div>
  );

  const BigTools = (
    <div className="flex items-center justify-center gap-s">
      {blankPage ? null : (
      <ObjectButton
        label="Fill a shape"
        active={tool === 'fill'}
        onClick={() => {
          setTool('fill');
          Feedback.pick();
          say('Fill — tap a shape to pour colour into it');
        }}
      >
        <FillObject ink={hex} />
      </ObjectButton>
      )}
      <ObjectButton
        label="Rub out"
        active={tool === 'erase'}
        onClick={() => {
          setTool('erase');
          Feedback.pick();
          say('Rubber — this only removes your own marks');
        }}
      >
        <EraserObject />
      </ObjectButton>
    </div>
  );

  const Palette = (
    <div
      role="group"
      aria-label="Colours"
      className={cn(
        'flex items-center gap-s',
        // On a phone the colours are one row you swipe, so the picture keeps
        // the screen. On a tablet they wrap in the rail, where there is height
        // to spend.
        wide ? 'flex-wrap justify-center' : 'overflow-x-auto pb-xs',
      )}
    >
      {swatches.map((s) => {
        const active = s.hex.toLowerCase() === hex.toLowerCase();
        return (
          <button
            key={s.hex}
            onClick={() => {
              setHex(s.hex);
              if (tool === 'erase') setTool('brush');
              Feedback.pick();
              say(`${s.name} selected`);
            }}
            aria-label={s.name}
            aria-pressed={active}
            className={cn(
              'h-11 w-11 shrink-0 rounded-full border-2 transition-transform duration-150',
              active
                ? 'scale-110 border-ink shadow-cut ring-2 ring-magic ring-offset-2 ring-offset-card'
                : 'border-line hover:scale-105',
            )}
            style={{ background: s.hex }}
          />
        );
      })}
      <button
        onClick={() => setShowMore((v) => !v)}
        className="flex h-11 shrink-0 items-center gap-xs whitespace-nowrap rounded-full border-2 border-dashed border-line bg-card px-m font-display text-[13px] font-semibold text-ink-soft"
      >
        {showMore ? 'Fewer' : 'More colours'}
      </button>
    </div>
  );

  /* Finishes belong to the fill tool: a shape can be plain, glittery or
     patterned. They are shown only when a fill is what the next tap will do,
     so a child is never offered a control that would do nothing. */
  const Finishes = tool === 'fill' && !blankPage && (
    <div
      role="group"
      aria-label="Fill style"
      className={cn('flex items-center gap-s', wide ? 'flex-wrap justify-center' : 'overflow-x-auto pb-xs')}
    >
      {FINISHES.map((f) => (
        <button
          key={f.id}
          onClick={() => {
            setFinish(f.id);
            Feedback.pick();
            say(`${f.name} fill`);
          }}
          aria-pressed={finish === f.id}
          // "Glitter" is also the name of a crayon in the tray. A finish and a
          // tool that sound identical are two different things to a screen
          // reader user and to a parent reading the screen aloud, so the
          // finishes say what they are.
          aria-label={`${f.name} fill`}
          className={cn(
            'h-11 w-11 shrink-0 overflow-hidden rounded-full border-2 transition-transform duration-150',
            finish === f.id
              ? 'scale-110 border-ink shadow-cut ring-2 ring-magic ring-offset-2 ring-offset-card'
              : 'border-line hover:scale-105',
          )}
        >
          <FinishSwatch finish={f.id} hex={hex} />
        </button>
      ))}
    </div>
  );

  const BrushSizes = tool !== 'fill' && (
    <div className="flex items-center justify-center gap-s" role="group" aria-label="Line thickness">
      {BRUSH_SIZES.map((b) => (
        <button
          key={b.value}
          onClick={() => {
            setBrushSize(b.value);
            Feedback.pick();
          }}
          aria-pressed={brushSize === b.value}
          aria-label={`${b.label} line`}
          className={cn(
            'flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full border-2 transition-transform duration-150',
            brushSize === b.value
              ? 'scale-105 border-primary bg-primary-soft text-primary-deep'
              : 'border-line bg-card text-ink-soft',
          )}
        >
          <span
            aria-hidden
            className="rounded-full bg-current"
            style={{ width: b.value / 3.2, height: b.value / 3.2 }}
          />
        </button>
      ))}
    </div>
  );

  const History = (
    <div className="flex items-center justify-center gap-s">
      <IconButton
        label="Undo"
        disabled={!engine.canUndo}
        onClick={() => {
          engine.undo();
          Feedback.undo();
          bump();
          onRequestSave();
        }}
      >
        <IconUndo />
      </IconButton>
      <IconButton
        label="Redo"
        disabled={!engine.canRedo}
        onClick={() => {
          engine.redo();
          Feedback.pick();
          bump();
          onRequestSave();
        }}
      >
        <IconRedo />
      </IconButton>
    </div>
  );

  const StayInside = blankPage ? null : (
    <div className="flex shrink-0 items-center gap-s rounded-pebble border-2 border-line bg-card px-m py-s">
      <input
        id="stay-inside"
        type="checkbox"
        checked={stayInside}
        onChange={(e) => {
          setStayInside(e.target.checked);
          say(e.target.checked ? 'Marks stay inside the lines' : 'You can draw anywhere');
        }}
        className="h-6 w-6 shrink-0 accent-magic"
      />
      <label
        htmlFor="stay-inside"
        className="whitespace-nowrap font-display text-[14px] font-semibold text-ink-soft"
      >
        Stay in the lines
      </label>
    </div>
  );

  const Rail = (
    <>
      {MediaTray}
      <div className="flex flex-wrap items-center justify-center gap-s">
        {BigTools}
        {History}
      </div>
      {StayInside}
      {Palette}
      {Finishes}
      {BrushSizes}
    </>
  );

  return (
    <div className="flex h-[100dvh] flex-col bg-paper">
      {/* Top bar: Back, title, truthful saved indicator, Done (spec §7 C08).
          On a phone the saved state gets its own row so it is never truncated
          or wrapped — the claim it makes has to stay readable. */}
      <header className="shrink-0 border-b-2 border-line bg-card px-l py-m">
        <div className="flex items-center gap-m">
          <button
            onClick={onBack}
            aria-label="Back to the pictures"
            className="flex min-h-touch min-w-touch shrink-0 items-center justify-center gap-xs rounded-pebble border-2 border-line px-m font-display text-[15px] font-semibold text-ink-soft"
          >
            <IconBack aria-hidden />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h1 className="min-w-0 flex-1 truncate font-display text-[17px] font-semibold text-ink sm:text-[19px]">
            {template.title}
          </h1>
          {wide ? <SaveIndicator state={saveState} className="shrink-0" /> : null}
          <Button
            variant="magic"
            size="md"
            onClick={() => {
              Feedback.finish();
              onDone();
            }}
            loading={busy}
            icon={<IconCheck />}
          >
            Done
          </Button>
        </div>
        {!wide ? <SaveIndicator state={saveState} className="mt-xs" /> : null}
      </header>

      <div className={cn('flex min-h-0 flex-1', wide ? 'flex-row' : 'flex-col')}>
        {wide && handedness === 'left' ? <SideRail>{Rail}</SideRail> : null}

        <main className="min-h-0 flex-1 p-m sm:p-l">
          <StudioCanvas
            engine={engine}
            tool={tool}
            brush={brush}
            color={paint}
            brushSize={brushSize}
            zoom={zoom}
            stayInside={stayInside && !blankPage}
            calm={quiet}
            onOperation={handleOperation}
            label={`${template.title} — colouring canvas. Current colour ${swatchName(hex)}, ${
              tool === 'fill' ? 'fill' : tool === 'erase' ? 'rubber' : medium(brush).name
            }.`}
          />
        </main>

        {wide && handedness === 'right' ? <SideRail>{Rail}</SideRail> : null}

        {!wide ? (
          <div className="shrink-0 border-t-2 border-line bg-card px-l pb-[max(12px,env(safe-area-inset-bottom))] pt-m">
            <div className="mb-s">{MediaTray}</div>
            <div className="mb-s flex items-center justify-between gap-s">
              {BigTools}
              {History}
            </div>
            <div className="mb-s">{Palette}</div>
            <div className="mb-s flex items-center gap-s">
              {StayInside}
              <div className="ml-auto">{BrushSizes}</div>
            </div>
            {Finishes ? <div className="mb-s">{Finishes}</div> : null}
          </div>
        ) : null}
      </div>

      {wide ? (
        <div className="flex shrink-0 items-center justify-center gap-s border-t-2 border-line bg-card px-l py-s">
          <span className="font-display text-[13px] font-semibold text-ink-faint">View</span>
          <button
            onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(2)))}
            className="min-h-touch min-w-touch rounded-pebble border-2 border-line font-display font-semibold"
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="w-14 text-center font-display text-[14px] tabular-nums text-ink-soft">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.min(2, +(z + 0.2).toFixed(2)))}
            className="min-h-touch min-w-touch rounded-pebble border-2 border-line font-display font-semibold"
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            onClick={() => setZoom(1)}
            className="min-h-touch rounded-pebble border-2 border-line px-m font-display text-[14px] font-semibold text-ink-soft"
          >
            Reset view
          </button>
        </div>
      ) : null}

      <p ref={announce} aria-live="polite" className="u-sr" />
    </div>
  );
}

function SideRail({ children }: { children: React.ReactNode }) {
  return (
    <aside className="flex w-[276px] shrink-0 flex-col gap-m overflow-y-auto border-x-2 border-line bg-card p-m">
      {children}
    </aside>
  );
}

/** A big round button holding a drawn object, sized for a small hand. */
function ObjectButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={cn(
        'flex h-[52px] w-[72px] shrink-0 items-center justify-center rounded-blob border-2 p-xs transition-transform duration-200',
        active
          ? 'scale-105 border-magic bg-magic/12 shadow-press'
          : 'border-line bg-card opacity-85 hover:opacity-100',
      )}
    >
      {children}
    </button>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex min-h-touch min-w-touch items-center justify-center rounded-full border-2 border-line bg-card text-ink-soft transition active:scale-95 disabled:opacity-35"
    >
      <span aria-hidden>{children}</span>
    </button>
  );
}

/** Draws a real preview of a fill finish, using the same tile the canvas uses. */
function FinishSwatch({ finish, hex }: { finish: Finish; hex: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = 44;
    canvas.height = 44;
    ctx.fillStyle = fillStyleFor(ctx, paintSpec(hex, finish));
    ctx.fillRect(0, 0, 44, 44);
  }, [finish, hex]);
  return <canvas ref={ref} className="h-full w-full" aria-hidden />;
}

/**
 * The saved indicator (spec §7 C08, §9.3).
 *
 * "Saved on this device" and "Saved everywhere" are different claims and are
 * never used interchangeably. A local write that failed says so and offers a
 * way out rather than showing a reassuring tick.
 */
function SaveIndicator({ state, className }: { state: SaveState; className?: string }) {
  const map = {
    idle: { text: 'Not saved yet', tone: 'text-ink-faint' },
    saving: { text: 'Saving…', tone: 'text-ink-faint' },
    'saved-local': { text: 'Saved on this device', tone: 'text-leaf' },
    'saved-everywhere': { text: 'Saved everywhere', tone: 'text-leaf' },
    failed: { text: 'Could not save — your picture is still here', tone: 'text-secondary' },
  } as const;
  const entry = map[state.kind];
  return (
    <p
      className={cn('flex items-center gap-xs whitespace-nowrap text-[13px] font-semibold', entry.tone, className)}
      aria-live="polite"
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-2 w-2 rounded-full',
          state.kind === 'failed' ? 'bg-secondary' : state.kind.startsWith('saved') ? 'bg-leaf' : 'bg-ink-faint',
        )}
      />
      {entry.text}
    </p>
  );
}

export { parsePaint };
